/* Chakkiwale custom multigrain builder.
 * The customer picks grains, sets a weight against each one, picks a grind and adds the mix.
 * It goes into the cart as one line per grain, each line counted in units of 10 g
 * (quantity = grams / 10). Before anything is added the mix is registered with Flow, so the
 * shop can mill it as one bag instead of separate bags.
 *
 * The one law of this screen: ONLY THE TOTAL RESCALES.
 *   - typing a grain's weight moves that grain and nothing else
 *   - adding / swapping / removing leaves every other row where it was
 *   - changing the total moves every grain together and keeps the ratios
 * No advice is ever given about what a mix should contain.
 *
 * Plain JS, no build step. Matches assets/card-configurator.js in style. */
(function () {
  'use strict';
  if (window.ChakkiCustomMix) { window.ChakkiCustomMix.init(); return; }

  var STEP = 10;                 // whole tens of grams — the cart quantity is grams / 10
  var NEW_GRAIN_GRAMS = 500;     // every grain arrives at 500 g; the total control scales the mix
  var UNDO_MS = 8000;
  /* shop-side words a customer must never be shown */
  var BANNED = /\b(sku|line item|variant|component|fulfilment|fulfillment|zakya)\b/i;
  var DEFAULT_GRINDS = [
    { value: 'superfine', label: 'Superfine' },
    { value: 'fine', label: 'Fine' },
    { value: 'coarse', label: 'Coarse' },
    { value: 'laddu_coarse', label: 'Laddu Coarse' }
  ];

  /* ---------------- helpers ---------------- */
  function el(tag, cls, txt) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (txt != null) e.textContent = txt;
    return e;
  }
  function num(v) { var n = parseFloat(v); return isFinite(n) ? n : 0; }
  function round10(n) { return Math.round(n / STEP) * STEP; }
  function clampG(n) { return Math.max(STEP, round10(n)); }
  function stepFor(g) { return g >= 1000 ? 100 : (g >= 200 ? 50 : 10); }

  function stripZeros(n) {
    var s = Number(n).toFixed(2);
    if (s.indexOf('.') > -1) s = s.replace(/0+$/, '').replace(/\.$/, '');
    return s;
  }
  /* "5 kg", "500 g", "1.25 kg" — number, space, lowercase unit, trailing zeros stripped */
  function fmtWeight(g) {
    g = Math.round(g);
    if (g >= 1000) return stripZeros(g / 1000) + ' kg';
    return g + ' g';
  }
  function fmtKg(g) { return stripZeros(Math.round(g) / 1000); }
  function fmtPct(p) {
    if (!isFinite(p) || p <= 0) return '0%';
    var v = p >= 10 ? Math.round(p) : Math.round(p * 10) / 10;
    return stripZeros(v) + '%';
  }
  function money(paise) {
    var r = Math.round(paise) / 100;
    var s = r % 1 === 0 ? r.toLocaleString('en-IN') : r.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return '₹' + s;
  }
  /* prices are always stated per kilo — never per 10 g */
  function perKgLabel(perKg) { return money(num(perKg) * 100) + '/kg'; }
  function costPaise(perKg, grams) { return Math.round(num(perKg) * grams / 10); }

  function jsonPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) { var e = new Error('request failed'); e.data = data; e.status = r.status; throw e; }
        return data;
      });
    });
  }

  /* ---------------- the builder ---------------- */
  function Builder(root) {
    if (root.__cmx) return;
    root.__cmx = this;
    this.root = root;

    var settings = {};
    var dataNode = root.querySelector('.cmx-data');
    if (dataNode) { try { settings = JSON.parse(dataNode.textContent); } catch (e) { settings = {}; } }
    this.settings = settings;
    this.base = String(settings.flowBaseUrl || '').replace(/\/+$/, '');

    this.cfg = null;
    this.rows = [];            // [{ grain, grams }] — in the order the customer added them
    this.unit = 'g';           // 'g' | 'pct'
    this.basis = 0;            // percent mode only: the total the shares are read against
    this.grind = null;
    this.busy = false;
    this.done = false;
    this.undo = null;
    this.undoTimer = null;
    this.picker = null;

    this.el = {
      body: root.querySelector('.cmx-body'),
      loading: root.querySelector('.cmx-state-loading'),
      error: root.querySelector('.cmx-state-error'),
      errorMsg: root.querySelector('.cmx-state-msg'),
      retry: root.querySelector('.cmx-retry'),
      units: root.querySelectorAll('.cmx-unit'),
      add: root.querySelector('.cmx-add'),
      empty: root.querySelector('.cmx-empty'),
      rows: root.querySelector('.cmx-rows'),
      share: root.querySelector('.cmx-share'),
      totalCard: root.querySelector('.cmx-card-total'),
      weights: root.querySelector('.cmx-weights'),
      totalInput: root.querySelector('.cmx-total-input'),
      grinds: root.querySelector('.cmx-grinds'),
      sumCount: root.querySelector('.cmx-sum-count'),
      sumWeight: root.querySelector('.cmx-sum-weight'),
      sumGrind: root.querySelector('.cmx-sum-grind'),
      sumMini: root.querySelector('.cmx-sum-mini'),
      sumPrice: root.querySelector('.cmx-sum-price'),
      cta: root.querySelector('.cmx-cta'),
      ctaNote: root.querySelector('.cmx-cta-note'),
      donePanel: root.querySelector('.cmx-done'),
      again: root.querySelector('.cmx-again'),
      undo: root.querySelector('.cmx-undo'),
      undoText: root.querySelector('.cmx-undo-text'),
      undoBtn: root.querySelector('.cmx-undo-btn')
    };

    this.bind();
    this.loadConfig();
  }

  /* ---------------- config ---------------- */
  Builder.prototype.loadConfig = function () {
    var self = this;
    this.showState('loading');
    fetch(this.base + '/shopify_integration/blend/config/', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('config'); return r.json(); })
      .then(function (cfg) {
        if (!cfg || !cfg.grains || !cfg.grains.length) throw new Error('empty');
        self.cfg = {
          grains: cfg.grains,                                     // already in the shop's order — never re-sorted
          minGrains: cfg.min_grains || 2,
          minTotal: cfg.min_total_grams || 1000,
          weights: (cfg.weights_kg && cfg.weights_kg.length ? cfg.weights_kg : ['1', '2', '3', '5'])
            .map(function (w) { return Math.round(num(w) * 1000); })
            .filter(function (g) { return g > 0; }),
          grinds: (cfg.grinds && cfg.grinds.length ? cfg.grinds : DEFAULT_GRINDS),
          defaultGrind: cfg.default_grind || 'fine'
        };
        self.grind = self.cfg.defaultGrind;
        if (!self.cfg.grinds.some(function (g) { return g.value === self.grind; })) self.grind = self.cfg.grinds[0].value;
        self.showState('ready');
        self.renderWeights();
        self.renderGrinds();
        self.renderAll();
      })
      .catch(function () {
        self.showState('error', 'We could not load the grain list just now. Please check your connection and try again.');
      });
  };

  Builder.prototype.showState = function (state, msg) {
    this.el.loading.hidden = state !== 'loading';
    this.el.error.hidden = state !== 'error';
    this.el.body.hidden = state !== 'ready';
    if (state === 'error' && msg) this.el.errorMsg.textContent = msg;
  };

  /* ---------------- static wiring ---------------- */
  Builder.prototype.bind = function () {
    var self = this;
    if (this.el.retry) this.el.retry.addEventListener('click', function () { self.loadConfig(); });
    if (this.el.add) this.el.add.addEventListener('click', function () { self.openPicker(null); });
    [].forEach.call(this.el.units, function (btn) {
      btn.addEventListener('click', function () { self.setUnit(btn.getAttribute('data-unit')); });
    });
    if (this.el.totalInput) {
      this.el.totalInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); this.blur(); } });
      this.el.totalInput.addEventListener('change', function () { self.commitTotal(this.value); });
      this.el.totalInput.addEventListener('blur', function () { self.commitTotal(this.value); });
    }
    if (this.el.cta) this.el.cta.addEventListener('click', function () { self.onCta(); });
    if (this.el.again) this.el.again.addEventListener('click', function () { self.reset(); });
    if (this.el.undoBtn) this.el.undoBtn.addEventListener('click', function () { self.doUndo(); });
  };

  /* ---------------- derived numbers ---------------- */
  Builder.prototype.total = function () {
    return this.rows.reduce(function (a, r) { return a + r.grams; }, 0);
  };
  /* what the shares are read against: the frozen basis in percent mode, the live total otherwise */
  Builder.prototype.basisGrams = function () {
    if (this.unit === 'pct' && this.basis > 0) return this.basis;
    return this.total();
  };
  Builder.prototype.priceP = function () {
    return this.rows.reduce(function (a, r) { return a + costPaise(r.grain.per_kg, r.grams); }, 0);
  };
  Builder.prototype.grindLabel = function () {
    var self = this;
    var g = (this.cfg ? this.cfg.grinds : DEFAULT_GRINDS).filter(function (x) { return x.value === self.grind; })[0];
    return g ? g.label : '';
  };
  Builder.prototype.hasGrain = function (variantId) {
    return this.rows.some(function (r) { return String(r.grain.variant_id) === String(variantId); });
  };

  /* ---------------- units ---------------- */
  Builder.prototype.setUnit = function (unit) {
    if (unit === this.unit) return;
    this.unit = unit;
    // Percent needs a number to be a percent OF. That is the total the customer
    // set -- not whatever the grains happen to add up to at this instant, and
    // never a single grain's weight.
    this.basis = unit === 'pct' ? (this.setTotalG || this.total()) : 0;
    [].forEach.call(this.el.units, function (b) {
      var on = b.getAttribute('data-unit') === unit;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.patch();          // same rows, new numbers — no rebuild, no layout shift
  };

  /* ---------------- total: the only thing that rescales ---------------- */
  Builder.prototype.renderWeights = function () {
    var self = this;
    this.el.weights.innerHTML = '';
    this.cfg.weights.forEach(function (g) {
      var b = el('button', 'cmx-weight', fmtWeight(g));
      b.type = 'button';
      b.setAttribute('data-grams', g);
      b.addEventListener('click', function () { self.setTotal(g); });
      self.el.weights.appendChild(b);
    });
  };

  Builder.prototype.commitTotal = function (raw) {
    var kg = num(String(raw).replace(/[^0-9.]/g, ''));
    if (kg <= 0) { this.patch(); return; }
    this.setTotal(Math.round(kg * 1000));
  };

  /* every grain moves together, the ratios hold, and the rounding remainder
     lands on the biggest grain so the weights still sum to the total asked for */
  Builder.prototype.setTotal = function (newTotal) {
    var rows = this.rows;
    if (!rows.length) return;
    var old = this.total();
    newTotal = Math.max(round10(newTotal), rows.length * STEP);
    // The bag the customer asked for. Percent reads against THIS, so a share
    // typed later means what they think it means.
    this.setTotalG = newTotal;
    for (var q = 0; q < rows.length; q++) rows[q].pctTyped = null;
    // nothing to move: leave the percent basis exactly where it was, so a stray
    // blur on the total field can never shift the shares under the customer
    if (old <= 0 || newTotal === old) { this.patch(); return; }
    this.done = false;

    var scaled = rows.map(function (r) { return Math.max(STEP, round10(r.grams / old * newTotal)); });
    var sum = scaled.reduce(function (a, b) { return a + b; }, 0);
    var diff = newTotal - sum;
    if (diff !== 0) {
      var bi = 0;
      scaled.forEach(function (v, i) { if (v > scaled[bi]) bi = i; });
      if (scaled[bi] + diff >= STEP) scaled[bi] += diff;
    }
    rows.forEach(function (r, i) { r.grams = scaled[i]; });
    if (this.unit === 'pct') this.basis = newTotal;   // the shares stay exactly where they were
    this.patch();
  };

  /* ---------------- grind ---------------- */
  Builder.prototype.renderGrinds = function () {
    var self = this;
    this.el.grinds.innerHTML = '';
    this.cfg.grinds.forEach(function (g) {
      var b = el('button', 'cmx-grind', g.label);
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.setAttribute('data-value', g.value);
      b.addEventListener('click', function () {
        self.grind = g.value;
        self.done = false;
        self.markGrinds();
        self.patch();
      });
      self.el.grinds.appendChild(b);
    });
    // radiogroup keyboard: roving tabindex + arrow keys
    this.el.grinds.addEventListener('keydown', function (e) {
      var chips = [].slice.call(self.el.grinds.querySelectorAll('.cmx-grind'));
      var i = chips.indexOf(document.activeElement);
      if (i === -1 || !chips.length) return;
      var n = -1;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % chips.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + chips.length) % chips.length;
      if (n > -1) { e.preventDefault(); chips[n].click(); chips[n].focus(); }
    });
    this.markGrinds();
  };
  Builder.prototype.markGrinds = function () {
    var self = this;
    [].forEach.call(this.el.grinds.querySelectorAll('.cmx-grind'), function (b) {
      var on = b.getAttribute('data-value') === self.grind;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    if (!this.el.grinds.querySelector('.cmx-grind[tabindex="0"]')) {
      var first = this.el.grinds.querySelector('.cmx-grind');
      if (first) first.tabIndex = 0;
    }
  };

  /* ---------------- rows: rebuilt only when grains come or go ---------------- */
  Builder.prototype.renderAll = function () {
    var self = this;
    var host = this.el.rows;
    host.innerHTML = '';
    this.el.empty.hidden = this.rows.length > 0;
    this.el.totalCard.hidden = this.rows.length === 0;

    this.rows.forEach(function (row) { host.appendChild(self.buildRow(row)); });
    this.markGrinds();
    this.patch();
  };

  Builder.prototype.buildRow = function (row) {
    var self = this;
    var name = row.grain.name;
    var refs = {};
    var node = el('div', 'cmx-row');
    node.setAttribute('data-variant-id', row.grain.variant_id);

    var top = el('div', 'cmx-row-top');
    var names = el('div', 'cmx-row-names');
    names.appendChild(el('span', 'cmx-row-name', name));       // never truncated — it wraps
    var sub = [row.grain.tag, perKgLabel(row.grain.per_kg)].filter(Boolean).join(' · ');
    names.appendChild(el('span', 'cmx-row-sub', sub));
    top.appendChild(names);
    // fixed-width figures column: 3% -> 100% must never shove the name sideways
    var figs = el('div', 'cmx-row-figs');
    refs.pct = el('span', 'cmx-row-pct', '0%');
    refs.cost = el('span', 'cmx-row-cost', money(0));
    figs.appendChild(refs.pct);
    figs.appendChild(refs.cost);
    top.appendChild(figs);
    node.appendChild(top);

    var bar = el('div', 'cmx-bar');
    refs.fill = el('span', 'cmx-bar-fill');
    bar.appendChild(refs.fill);
    node.appendChild(bar);

    var controls = el('div', 'cmx-row-controls');

    var stepper = el('div', 'cmx-stepper');
    var minus = el('button', 'cmx-step', '−');
    minus.type = 'button';
    minus.setAttribute('aria-label', 'Less ' + name);
    var input = el('input', 'cmx-amount');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.autocomplete = 'off';
    input.setAttribute('aria-label', name + ' amount');
    refs.unit = el('span', 'cmx-amount-unit', 'g');
    var plus = el('button', 'cmx-step', '+');
    plus.type = 'button';
    plus.setAttribute('aria-label', 'More ' + name);
    stepper.appendChild(minus);
    stepper.appendChild(input);
    stepper.appendChild(refs.unit);
    stepper.appendChild(plus);
    controls.appendChild(stepper);

    var acts = el('div', 'cmx-row-acts');
    var swap = el('button', 'cmx-act cmx-act-swap', 'Swap');
    swap.type = 'button';
    swap.setAttribute('aria-label', 'Swap ' + name + ' for another grain');
    var remove = el('button', 'cmx-act cmx-act-remove', 'Remove');
    remove.type = 'button';
    remove.setAttribute('aria-label', 'Remove ' + name);
    acts.appendChild(swap);
    acts.appendChild(remove);
    controls.appendChild(acts);
    node.appendChild(controls);

    refs.input = input;
    refs.node = node;
    row.el = refs;

    minus.addEventListener('click', function () { self.nudge(row, -1); });
    plus.addEventListener('click', function () { self.nudge(row, 1); });
    input.addEventListener('input', function () { self.typeAmount(row, input.value, false); });
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); input.blur(); } });
    input.addEventListener('blur', function () { self.typeAmount(row, input.value, true); });
    swap.addEventListener('click', function () { self.openPicker(self.rows.indexOf(row)); });
    remove.addEventListener('click', function () { self.removeRow(row); });

    return node;
  };

  /* one grain moves, nothing else does */
  Builder.prototype.typeAmount = function (row, raw, commit) {
    var v = num(String(raw).replace(/[^0-9.]/g, ''));
    this.done = false;
    if (this.unit === 'pct') {
      var basis = this.basisGrams();
      row.grams = basis > 0 ? clampG(v / 100 * basis) : clampG(v);
      row.pctTyped = v;          // theirs; never rewritten back at them
    } else {
      row.grams = clampG(v);
      row.pctTyped = null;
    }
    this.patch(commit ? null : row);   // while typing, leave the field the customer is in alone
  };

  Builder.prototype.nudge = function (row, dir) {
    this.done = false;
    row.pctTyped = null;      // the weight moved, so the old typed share is stale
    if (this.unit === 'pct') {
      var basis = this.basisGrams();
      if (basis > 0) {
        var next = Math.max(0, Math.round(row.grams / basis * 100) + dir);
        row.grams = clampG(next / 100 * basis);
      } else {
        row.grams = clampG(row.grams + dir * STEP);
      }
    } else {
      row.grams = clampG(row.grams + dir * stepFor(row.grams));
    }
    this.patch();
  };

  /* ---------------- add / swap / remove — every other row is left alone ---------------- */
  Builder.prototype.addGrain = function (grain) {
    this.rows.push({ grain: grain, grams: NEW_GRAIN_GRAMS });
    this.done = false;
    // Deliberately NOT adopting a basis here. A basis taken from a single
    // grain means the first grain is 100% by definition, and every share the
    // customer then types is read against one grain's weight rather than the
    // bag they have in mind. The basis is the TOTAL they set; until they set
    // one, percent has nothing to be a percent of.
    this.renderAll();
    var last = this.el.rows.lastElementChild;
    if (last) {
      last.classList.add('is-new');
      setTimeout(function () { last.classList.remove('is-new'); }, 900);
    }
  };
  Builder.prototype.swapGrain = function (index, grain) {
    var row = this.rows[index];
    if (!row) return;
    row.grain = grain;          // the weight stays exactly where it was
    this.done = false;
    this.renderAll();
  };
  Builder.prototype.removeRow = function (row) {
    var i = this.rows.indexOf(row);
    if (i < 0) return;
    this.rows.splice(i, 1);
    this.done = false;
    this.showUndo({ index: i, grain: row.grain, grams: row.grams });
    this.renderAll();
  };
  Builder.prototype.showUndo = function (entry) {
    var self = this;
    this.undo = entry;
    this.el.undoText.textContent = 'Removed ' + entry.grain.name;
    this.el.undo.hidden = false;
    clearTimeout(this.undoTimer);
    this.undoTimer = setTimeout(function () { self.hideUndo(); }, UNDO_MS);
  };
  Builder.prototype.hideUndo = function () {
    clearTimeout(this.undoTimer);
    this.undo = null;
    this.el.undo.hidden = true;
  };
  Builder.prototype.doUndo = function () {
    if (!this.undo) return;
    var u = this.undo;
    this.hideUndo();
    this.rows.splice(Math.min(u.index, this.rows.length), 0, { grain: u.grain, grams: u.grams });
    this.renderAll();
  };

  /* ---------------- patch: numbers in place, no layout shift ---------------- */
  Builder.prototype.patch = function (skipRow) {
    var self = this;
    var total = this.total();
    var basis = this.basisGrams();

    this.rows.forEach(function (row) {
      var e = row.el;
      if (!e) return;
      var share = basis > 0 ? row.grams / basis * 100 : 0;
      e.pct.textContent = fmtPct(share);
      e.fill.style.width = Math.max(0, Math.min(100, share)) + '%';
      e.cost.textContent = money(costPaise(row.grain.per_kg, row.grams));
      if (self.unit === 'pct') {
        // The unit slot is fixed width and holds ONE character. A live weight
        // in here ('% · 1.23 kg') changes width as the number does, and the
        // stepper shifts under the finger -- the exact fault rule 8 exists for.
        // The weight is already on the row, next to the share bar.
        e.unit.textContent = '%';
        // What the customer typed STAYS. Converting their share to grams,
        // snapping to tens and converting back produces a slightly different
        // number, and writing that back is auto-correction -- which percent
        // mode is not allowed to do. The grams are the truth and are shown
        // beside the bar; the typed share is theirs.
        if (row !== skipRow && document.activeElement !== e.input) {
          if (row.pctTyped != null) e.input.value = stripZeros(row.pctTyped);
          else e.input.value = stripZeros(basis > 0 ? Math.round(row.grams / basis * 1000) / 10 : 0);
        }
      } else {
        e.unit.textContent = 'g';
        if (row !== skipRow && document.activeElement !== e.input) e.input.value = String(row.grams);
      }
    });

    // percent mode states the position and never corrects it
    if (this.unit === 'pct' && this.rows.length && basis > 0) {
      var pct = Math.round(total / basis * 100);
      var line = 'The shares come to ' + pct + '% of ' + fmtWeight(basis);
      if (pct < 100) line += ' — ' + (100 - pct) + '% still to go.';
      else if (pct > 100) line += ' — ' + (pct - 100) + '% over.';
      else line += '.';
      this.el.share.textContent = line;
      this.el.share.hidden = false;
    } else {
      this.el.share.hidden = true;
      this.el.share.textContent = '';
    }

    // total control
    [].forEach.call(this.el.weights.querySelectorAll('.cmx-weight'), function (b) {
      b.classList.toggle('is-on', Number(b.getAttribute('data-grams')) === total);
    });
    if (document.activeElement !== this.el.totalInput) this.el.totalInput.value = fmtKg(total);

    // summary
    this.el.sumCount.textContent = String(this.rows.length);
    this.el.sumWeight.textContent = fmtWeight(total);
    this.el.sumGrind.textContent = this.grindLabel();
    this.el.sumPrice.textContent = money(this.priceP());
    this.el.sumMini.textContent = this.rows.length
      ? this.rows.length + (this.rows.length === 1 ? ' grain' : ' grains') + ' · ' + fmtWeight(total) + ' · ' + this.grindLabel()
      : 'No grains yet';

    this.renderCta();
  };

  /* ---------------- the button always says what it is waiting for ---------------- */
  Builder.prototype.blocker = function () {
    var min = this.cfg ? this.cfg.minGrains : 2;
    var minTotal = this.cfg ? this.cfg.minTotal : 1000;
    if (!this.rows.length) return { kind: 'grains', label: 'Add the grains you want' };
    if (this.rows.length < min) {
      return {
        kind: 'grains',
        label: min === 2
          ? 'Add one more grain — a mix needs at least two'
          : 'Add more grains — a mix needs at least ' + min
      };
    }
    if (this.total() < minTotal) {
      return {
        kind: 'total',
        label: minTotal === 1000
          ? 'At least 1 kg of grain to mill a mix'
          : 'At least ' + fmtWeight(minTotal) + ' of grain to mill a mix'
      };
    }
    return null;
  };

  Builder.prototype.renderCta = function () {
    var cta = this.el.cta;
    if (this.done) {
      this.el.donePanel.hidden = false;
      cta.hidden = true;
      this.el.ctaNote.hidden = true;
      return;
    }
    this.el.donePanel.hidden = true;
    cta.hidden = false;
    var b = this.blocker();
    if (b) {
      cta.textContent = b.label;
      cta.classList.add('is-waiting');
    } else {
      cta.textContent = 'Add to cart · ' + money(this.priceP());
      cta.classList.remove('is-waiting');
    }
  };

  Builder.prototype.note = function (msg, kind) {
    var n = this.el.ctaNote;
    n.textContent = msg || '';
    n.hidden = !msg;
    n.className = 'cmx-cta-note' + (kind ? ' is-' + kind : '');
  };

  /* ---------------- add to cart ---------------- */
  Builder.prototype.onCta = function () {
    if (this.busy) return;
    var b = this.blocker();
    if (b) {                                   // never a dead button: it takes you to whatever is missing
      this.note('');
      if (b.kind === 'grains') this.openPicker(null);
      else if (this.el.totalInput) { this.el.totalInput.focus(); this.el.totalInput.select(); }
      return;
    }
    this.submit();
  };

  Builder.prototype.cartToken = function () {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (c && c.token) return c.token;
        return jsonPost('/cart/update.js', { attributes: {} }).then(function (c2) { return (c2 && c2.token) || ''; });
      });
  };

  Builder.prototype.submit = function () {
    var self = this;
    var rows = this.rows.slice();
    this.busy = true;
    this.note('');
    this.el.cta.classList.add('cmx-loading');

    this.cartToken()
      .then(function (token) {
        // register first, so a cart that gains lines already has the mix on record
        return jsonPost(self.base + '/shopify_integration/blend/register/', {
          cart_token: token,
          grind: self.grind,
          components: rows.map(function (r) { return { variant_gid: r.grain.variant_gid, grams: r.grams }; })
        });
      })
      .then(function (res) {
        if (!res || res.ok === false) throw new Error('register');
        self.blendId = res.blend_id;
        return jsonPost('/cart/add.js', {
          items: rows.map(function (r) { return { id: r.grain.variant_id, quantity: r.grams / STEP }; })
        });
      })
      .then(function () {
        self.busy = false;
        self.done = true;
        self.el.cta.classList.remove('cmx-loading');
        self.refreshCart();
        self.renderCta();
        if (self.el.donePanel && self.el.donePanel.scrollIntoView) {
          try { self.el.donePanel.scrollIntoView({ block: 'nearest' }); } catch (e) {}
        }
      })
      .catch(function (err) {
        self.busy = false;
        self.el.cta.classList.remove('cmx-loading');
        var data = err && err.data;
        var msg = data ? (data.description || data.message) : '';
        // a message from a server is only shown if it is in words a customer should read
        if (!msg || BANNED.test(String(msg))) {
          msg = 'We could not add your mix just now. Please check your connection and try again.';
        }
        self.note(msg, 'error');
      });
  };

  Builder.prototype.refreshCart = function () {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.js-cart-count, [data-cart-count]').forEach(function (e) { e.textContent = cart.item_count; });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        try {
          if (window.theme && theme.miniCart && theme.miniCart.generateCart) theme.miniCart.generateCart();
        } catch (e) {}
      })
      .catch(function () {});
  };

  Builder.prototype.reset = function () {
    var self = this;
    this.rows = [];
    this.unit = 'g';
    this.basis = 0;
    this.done = false;
    this.hideUndo();
    this.note('');
    [].forEach.call(this.el.units, function (b) {
      var on = b.getAttribute('data-unit') === 'g';
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    this.grind = this.cfg ? this.cfg.defaultGrind : 'fine';
    if (this.cfg && !this.cfg.grinds.some(function (g) { return g.value === self.grind; })) this.grind = this.cfg.grinds[0].value;
    this.renderAll();
    if (this.el.add) this.el.add.focus();
  };

  /* ---------------- the grain picker: it takes over the sheet ---------------- */
  Builder.prototype.openPicker = function (swapIndex) {
    var self = this;
    if (!this.cfg) return;
    this.closePicker();
    var swapping = swapIndex != null && !!this.rows[swapIndex];
    var current = swapping ? this.rows[swapIndex].grain : null;

    var rootEl = el('div', 'cmx-picker-root');
    var scrim = el('div', 'cmx-picker-scrim');
    scrim.addEventListener('click', function () { self.closePicker(); });

    var sheet = el('div', 'cmx-picker');
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', swapping ? 'Swap ' + current.name : 'Add a grain');

    var head = el('div', 'cmx-picker-head');                 // fixed head: title + search, always above the results
    var titleRow = el('div', 'cmx-picker-titlerow');
    titleRow.appendChild(el('h3', 'cmx-picker-title', swapping ? 'Swap ' + current.name : 'Add a grain'));
    var close = el('button', 'cmx-picker-close', '✕');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.addEventListener('click', function () { self.closePicker(); });
    titleRow.appendChild(close);
    head.appendChild(titleRow);

    var search = el('input', 'cmx-search');
    search.type = 'search';
    search.placeholder = 'Search grains';
    search.setAttribute('aria-label', 'Search grains');
    search.autocomplete = 'off';
    head.appendChild(search);
    sheet.appendChild(head);

    var list = el('div', 'cmx-picker-list');
    sheet.appendChild(list);

    rootEl.appendChild(scrim);
    rootEl.appendChild(sheet);
    document.body.appendChild(rootEl);
    document.body.classList.add('cmx-locked');

    this.picker = {
      root: rootEl,
      list: list,
      search: search,
      swapIndex: swapping ? swapIndex : null,
      returnFocus: document.activeElement
    };

    search.addEventListener('input', function () { self.renderPickerList(search.value); });
    this.renderPickerList('');

    this.picker.onKey = function (e) { if (e.key === 'Escape') { e.preventDefault(); self.closePicker(); } };
    document.addEventListener('keydown', this.picker.onKey);

    this.picker.onViewport = function () { self.sizePicker(); };
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.picker.onViewport);
      window.visualViewport.addEventListener('scroll', this.picker.onViewport);
    }
    window.addEventListener('resize', this.picker.onViewport);
    this.sizePicker();

    // desktop: search is ready to type into. on a phone the list shows first, keypad only on tap.
    if (window.matchMedia('(min-width: 768px)').matches) search.focus();
    else close.focus();
  };

  /* keeps the sheet inside whatever space the phone keyboard leaves.
     the sheet is fixed, so it is laid out against the layout viewport: the height is
     clamped to what is left below the offset or it would hang off the bottom of the screen */
  Builder.prototype.sizePicker = function () {
    if (!this.picker) return;
    var vv = window.visualViewport;
    var layout = document.documentElement.clientHeight || window.innerHeight;
    var top = vv ? Math.max(0, vv.offsetTop) : 0;
    var h = vv ? vv.height : window.innerHeight;
    h = Math.max(240, Math.min(h, layout - top));
    this.picker.root.style.setProperty('--cmx-vh', h + 'px');
    this.picker.root.style.setProperty('--cmx-vtop', top + 'px');
  };

  Builder.prototype.renderPickerList = function (query) {
    var self = this;
    var p = this.picker;
    if (!p) return;
    var q = String(query || '').trim().toLowerCase();
    var current = p.swapIndex != null && this.rows[p.swapIndex] ? this.rows[p.swapIndex].grain : null;

    // the server order is the shop's order — filter only, never re-sort
    var matches = this.cfg.grains.filter(function (g) {
      if (!q) return true;
      return String(g.name).toLowerCase().indexOf(q) > -1 || String(g.tag || '').toLowerCase().indexOf(q) > -1;
    });

    p.list.innerHTML = '';
    if (!matches.length) {
      p.list.appendChild(el('p', 'cmx-picker-none', 'No grain matches “' + query + '”.'));
      return;
    }

    matches.forEach(function (g) {
      var isCurrent = !!current && String(current.variant_id) === String(g.variant_id);
      var inMix = !isCurrent && self.hasGrain(g.variant_id);
      var b = el('button', 'cmx-pick' + (inMix ? ' is-in' : '') + (isCurrent ? ' is-current' : ''));
      b.type = 'button';

      var main = el('span', 'cmx-pick-main');
      main.appendChild(el('span', 'cmx-pick-name', g.name));
      if (g.tag) main.appendChild(el('span', 'cmx-pick-tag', g.tag));
      b.appendChild(main);

      var side = el('span', 'cmx-pick-side');
      side.appendChild(el('span', 'cmx-pick-rate', perKgLabel(g.per_kg)));
      if (isCurrent) side.appendChild(el('span', 'cmx-pick-state', 'In this row'));
      else if (inMix) side.appendChild(el('span', 'cmx-pick-state', 'In your mix'));
      b.appendChild(side);

      if (inMix) {
        b.disabled = true;
        b.setAttribute('aria-disabled', 'true');
      } else {
        b.addEventListener('click', function () {
          var idx = p.swapIndex;
          self.closePicker(true);
          if (idx != null) self.swapGrain(idx, g);
          else self.addGrain(g);
        });
      }
      p.list.appendChild(b);
    });
  };

  Builder.prototype.closePicker = function (backToList) {
    var p = this.picker;
    if (!p) return;
    this.picker = null;
    document.removeEventListener('keydown', p.onKey);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', p.onViewport);
      window.visualViewport.removeEventListener('scroll', p.onViewport);
    }
    window.removeEventListener('resize', p.onViewport);
    if (p.root.parentNode) p.root.parentNode.removeChild(p.root);
    document.body.classList.remove('cmx-locked');
    var back = backToList ? this.el.add : p.returnFocus;
    if (back && back.focus) { try { back.focus(); } catch (e) {} }
  };

  /* ---------------- init ---------------- */
  function init(scope) {
    (scope || document).querySelectorAll('.cmx').forEach(function (e) { new Builder(e); });
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });

  window.ChakkiCustomMix = { init: init };
})();
